import * as React from 'react';
import { useCallback } from 'react';

import { useStoreContext } from 'features/store';
import { ErrorRemovingLastFavoriteStoreModal } from 'features/store/components/modal-error-removing-last-favorite-store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useStoreDiagnostics } from 'features/store/hooks/use-store-diagnostics';
import { useNonTraditionalStoreUI } from 'features/store/pages/pickup-store-selector/hooks/use-non-traditional-store';
import { useStoreActions } from 'features/store/pages/pickup-store-selector/hooks/use-store-actions';

import { useFavoriteStore } from 'hooks/favorite-stores';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import {
  LogUserErrorSeverity,
  useLogUserErrorMessageLazyContext,
} from 'hooks/log-user-error-message';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/types';

import { NonTraditionalStoreDialogDrawer } from '../non-traditional-store-dialog-drawer';

import { useErrorMessage } from './hooks/use-error-message';
import { useOpenClosedText } from './hooks/use-open-closed-text';
import { useServiceModeText } from './hooks/use-service-mode-text';
import { Wrapper } from './store-card.styled';
import { StoreCardView } from './store-card.view';
import { StoreCardProps } from './types';

/**
 * StoreCard container component. This component is intended to be used on the
 * store locator. Data orchestration should happen at this level while keeping
 * the view layer more presentational.
 */
export const StoreCard: React.FC<StoreCardProps> = React.memo(
  ({
    tab,
    restaurant,
    onCardPress,
    isSelected,
    isInMapView,
    isInSupportForm,
    onSelectStore,
    isLastItem,
    onFavoriteClick,
  }) => {
    const { store: activeRestaurant } = useStoreContext();

    const isDiagnosticsEnabled = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
    const { distanceText } = useDistanceText({
      restaurant,
      shouldUseUserCoordinatesIfAvailable: true,
    });
    const errorMessage = useErrorMessage({ restaurant });
    const { isStoreAvailableForPickup } = useStoreAvailabilityFns();

    const serviceModesText = useServiceModeText({ restaurant });
    const getStoreDiagnostics = useStoreDiagnostics();
    const { storeDiagnostics } = useUIContext();

    const { onInfoPress, onOrderPress } = useStoreActions({ restaurant });

    const {
      errorFavoriteStoreDialog,
      favStoreLoading,
      isFavorite,
      toggleFavorite,
      hasErrorRemovingLastFavStore,
      onDismissErrorRemovingLastFavStoreModal,
    } = useFavoriteStore(restaurant._id ?? '', restaurant.number ?? '');

    const { isOpen, isWithinOneHourOfClose, text } = useOpenClosedText(restaurant);

    const nonTraditionalStoreUI = useNonTraditionalStoreUI({ restaurant });

    const canBeSelected = isStoreAvailableForPickup(restaurant);
    useLogUserErrorMessageLazyContext({
      message: "Store not available: Store can't be selected",
      logWhenTrue: !canBeSelected && isOpen,
      severity: LogUserErrorSeverity.Informational,
      getContext: () => ({ ...getStoreDiagnostics(restaurant), source: 'storeLocator' }),
    });

    const handleOnCardPress = useCallback(() => onCardPress?.({ restaurant }), [
      onCardPress,
      restaurant,
    ]);
    // Determines whether to pre-fill a store selection form or proceed with a direct order.
    const handleOrderOrSelectStore = () => {
      const isActiveRestaurant = activeRestaurant === restaurant;
      startTrace(
        isActiveRestaurant
          ? TraceName.STORE_LOCATOR_ORDER_HERE_TO_MENU_ITEMS_DISPLAYED_STORE_ALREADY_SELECTED
          : TraceName.STORE_LOCATOR_ORDER_HERE_TO_MENU_ITEMS_DISPLAYED_STORE_NOT_ALREADY_SELECTED,
        { restaurantId: restaurant.id ?? '' }
      );
      if (onSelectStore && restaurant._id) {
        onSelectStore(restaurant._id);
      } else {
        onOrderPress();
      }
    };

    const handleOnOrderPress = () => {
      // If non-traditional store, prompt user with details before proceeding to order
      if (nonTraditionalStoreUI?.isNonTraditionalStore) {
        nonTraditionalStoreUI.openDrawer();
        return;
      }
      handleOrderOrSelectStore();
    };

    const handleOnConfirmNonTraditionalStoreDialog = () => {
      nonTraditionalStoreUI?.closeDrawer();
      handleOrderOrSelectStore();
    };

    const handleOnFavoriteClick = () => {
      toggleFavorite();
      onFavoriteClick?.();
    };

    return (
      <Wrapper focusable={false} testID="store-card" isSelected={false}>
        <StoreCardView
          tab={tab}
          onCardPress={handleOnCardPress}
          // note: this handler controls the visibility of the diagnostics button
          // thats why we need to check the flag here, otherwise the button will be visible
          onDiagnosticsClick={
            isDiagnosticsEnabled ? () => storeDiagnostics.openModal(restaurant) : undefined
          }
          onFavoriteClick={handleOnFavoriteClick}
          onInfoPress={onInfoPress}
          onOrderPress={handleOnOrderPress}
          isFavorite={isFavorite}
          isUpdatingFavorites={favStoreLoading}
          isSelected={isSelected}
          storeDetails={{
            serviceModesText,
            distanceText,
            restaurantStatus: {
              canBeSelected,
              isOpen,
              isWithinOneHourOfClose,
              text,
            },
            address: restaurant.physicalAddress?.address1,
            city: `${restaurant.physicalAddress?.city}, ${
              restaurant.physicalAddress?.stateProvinceShort ||
              restaurant.physicalAddress?.stateProvince ||
              ''
            }`,
            isHalal: Boolean(restaurant.isHalal),
            restaurant,
            isNonTraditionalStore: nonTraditionalStoreUI?.isNonTraditionalStore,
            accessInstructions: nonTraditionalStoreUI?.accessInstructions,
          }}
          errorMessage={errorMessage}
          isInMapView={isInMapView}
          isInSupportForm={isInSupportForm}
          isLastItem={isLastItem}
        />
        {hasErrorRemovingLastFavStore && (
          <ErrorRemovingLastFavoriteStoreModal
            onDismiss={onDismissErrorRemovingLastFavStoreModal}
          />
        )}
        {errorFavoriteStoreDialog}
        {nonTraditionalStoreUI && (
          <NonTraditionalStoreDialogDrawer
            onConfirm={handleOnConfirmNonTraditionalStoreDialog}
            onCancel={nonTraditionalStoreUI.closeDrawer}
            isOpen={nonTraditionalStoreUI.isDialogDrawerOpen}
            accessInstructions={nonTraditionalStoreUI.accessInstructions}
          />
        )}
      </Wrapper>
    );
  }
);
